import { Component, OnDestroy, OnInit } from '@angular/core';
import { EntityService, SharedService } from '@app/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HyperpureOrder } from '../../../hyperpure/interfaces';
import { HyperpureOrderService } from '../../../hyperpure/services/hyperpure-order.service';
import { DmartOrderService } from '../../services/dmart-order.service';
import { PurchaseOrderService } from '../../services/purchase-order.service';
import { DeliveritOrder, DeliveritOrderService } from '../../services/deliverit-order.service';

@Component({
    selector: 'app-pm-orders',
    templateUrl: './pm-orders.component.html',
    styleUrls: ['./pm-orders.component.scss']
})
export class PmOrdersComponent implements OnInit, OnDestroy {

    selectedEntityId = '';

    // Sub-tab
    activeSubTab: 'purchaseOrders' | 'hyperpureOrders' | 'udaanOrders' | 'dmartOrders' | 'deliveritOrders' = 'purchaseOrders';

    // Purchase Orders (Local Suppliers)
    purchaseOrders: any[] = [];
    isPoLoading = false;
    poPagination = {
        page: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0
    };
    poSearchQuery = '';

    // Hyperpure Orders
    hyperpureOrders: HyperpureOrder[] = [];
    isHpLoading = false;
    hpPagination = {
        currentPage: 1,
        pageSize: 20,
        totalOrders: 0,
        totalPages: 0
    };
    hpSearchTerm = '';
    hpSelectedStatus = '';
    hpSortBy = 'order_placed_at';
    hpSortOrder: 'asc' | 'desc' = 'desc';
    hpStatusOptions = [
        { label: 'All Status', value: '' },
        { label: 'Placed', value: 'PLACED' },
        { label: 'Delivered', value: 'DELIVERED' },
        { label: 'Cancelled', value: 'CANCELLED' },
        { label: 'Pending', value: 'PENDING' }
    ];

    // DMart Orders
    dmartOrders: any[] = [];
    isDmartLoading = false;
    dmartPagination = {
        page: 1,
        pageSize: 10,
        totalRecords: 0,
        totalPages: 0
    };
    dmartSearchQuery = '';

    // DeliverIt Orders
    deliveritOrders: DeliveritOrder[] = [];
    isDeliveritLoading = false;
    deliveritPagination = {
        currentPage: 1,
        pageSize: 20,
        totalOrders: 0,
        totalPages: 0
    };
    deliveritSearchTerm = '';
    deliveritSelectedStatus = '';
    deliveritSortBy = 'order_date';
    deliveritSortOrder: 'asc' | 'desc' = 'desc';
    deliveritStatusOptions = [
        { label: 'All Status', value: '' },
        { label: 'New', value: 'NEW' },
        { label: 'Pick-Pack', value: 'PICK-PACK' },
        { label: 'Invoicing', value: 'INVOICING' },
        { label: 'Assigned', value: 'ASSIGNED' },
        { label: 'Out For Delivery', value: 'OUT FOR DELIVERY' },
        { label: 'Delivered', value: 'DELIVERED' },
        { label: 'Returned', value: 'RETURNED' },
        { label: 'Closed', value: 'CLOSED' }
    ];

    private destroy$ = new Subject<void>();

    constructor(
        private entityService: EntityService,
        private sharedService: SharedService,
        private purchaseOrderService: PurchaseOrderService,
        private hyperpureOrderService: HyperpureOrderService,
        private dmartOrderService: DmartOrderService,
        private deliveritOrderService: DeliveritOrderService
    ) { }

    /**
     * Initializes the component and loads purchase orders for the selected entity.
     */
    ngOnInit(): void {
        const selectedEntity = this.entityService.getSingleSelectedEntity();
        if (selectedEntity) {
            this.selectedEntityId = selectedEntity.id;
        }

        // Load default sub-tab data
        this.fetchPurchaseOrders();
    }

    /**
     * Cleanup on component destruction.
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── Sub-Tab ──

    /**
     * Handles switching between Purchase Orders and Hyperpure Orders tabs.
     * @param subTab The sub-tab to switch to.
     */
    onSubTabChange(subTab: 'purchaseOrders' | 'hyperpureOrders' | 'udaanOrders' | 'dmartOrders' | 'deliveritOrders'): void {
        this.activeSubTab = subTab;
        if (subTab === 'purchaseOrders') {
            this.fetchPurchaseOrders();
        } else if (subTab === 'hyperpureOrders') {
            this.fetchHyperpureOrders();
        } else if (subTab === 'udaanOrders') {
            // Placeholder for Udaan orders fetch
            this.purchaseOrders = [];
        } else if (subTab === 'dmartOrders') {
            this.fetchDmartOrders();
        } else if (subTab === 'deliveritOrders') {
            this.fetchDeliveritOrders();
        }
    }

    // ── Purchase Orders ──

    /**
     * Fetches purchase orders from the local supplier PO service.
     * @param page The page number to fetch.
     */
    fetchPurchaseOrders(page: number = 1): void {
        if (!this.selectedEntityId) return;

        this.isPoLoading = true;
        this.poPagination.page = page;

        const params: any = {
            page: this.poPagination.page,
            pageSize: this.poPagination.pageSize,
            search: this.poSearchQuery
        };

        this.purchaseOrderService.getPurchaseOrders(params, this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    const items = response.items || response.data || [];
                    this.purchaseOrders = this.mapPurchaseOrderResponse(items);

                    const pagination = response.pagination || {};
                    this.poPagination.totalItems = pagination.total_items || pagination.totalItems || 0;
                    this.poPagination.totalPages = pagination.total_pages || pagination.totalPages || 0;

                    this.isPoLoading = false;
                },
                (error: any) => {
                    console.error('Failed to fetch purchase orders', error);
                    this.sharedService.errorAlert('Failed to load purchase orders');
                    this.isPoLoading = false;
                }
            );
    }

    /**
     * Maps raw purchase order response to a flatter structure for UI consumption.
     * @param items Raw PO items from API.
     * @returns Mapped PO items.
     */
    private mapPurchaseOrderResponse(items: any[]): any[] {
        return items.map(item => ({
            ...item,
            poNumber: item.po_number || item.poNumber,
            supplierName: item.supplier_name || item.supplierName,
            supplierAlias: item.supplier_alias || item.supplierAlias,
            totalAmount: item.total_amount || item.totalAmount,
        }));
    }

    /**
     * Handles pagination change for purchase orders.
     * @param page New page number.
     */
    onPoPageChange(page: number): void {
        this.poPagination.page = page;
        this.fetchPurchaseOrders(page);
    }

    /**
     * Resets pagination and fetches purchase orders based on search query.
     */
    onPoSearch(): void {
        this.poPagination.page = 1;
        this.fetchPurchaseOrders(1);
    }

    /**
     * Resets pagination and fetches purchase orders when page size changes.
     */
    onPoPageSizeChange(pageSize: number): void {
        this.poPagination.pageSize = pageSize;
        this.poPagination.page = 1;
        this.fetchPurchaseOrders(1);
    }




    // ── Hyperpure Orders ──

    /**
     * Fetches Hyperpure specific orders using the hyperpure order service.
     */
    fetchHyperpureOrders(): void {
        if (!this.selectedEntityId) return;

        this.isHpLoading = true;
        const params = {
            page: this.hpPagination.currentPage,
            limit: this.hpPagination.pageSize,
            search: this.hpSearchTerm,
            sortBy: this.hpSortBy,
            sortOrder: this.hpSortOrder,
            status: this.hpSelectedStatus
        };

        this.hyperpureOrderService.getOrders(params)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response.success) {
                        this.hyperpureOrders = response.data.orders;
                        this.hpPagination.totalOrders = response.data.pagination.total;
                        this.hpPagination.totalPages = response.data.pagination.total_pages || response.data.pagination.totalPages;
                    }
                    this.isHpLoading = false;
                },
                error: (error) => {
                    console.error('Error loading Hyperpure orders:', error);
                    this.isHpLoading = false;
                }
            });
    }

    /**
     * Triggers a new Hyperpure order fetch with reset pagination on search.
     */
    onHpSearch(): void {
        this.hpPagination.currentPage = 1;
        this.fetchHyperpureOrders();
    }

    /**
     * Triggers a new Hyperpure order fetch when status filter changes.
     */
    onHpStatusChange(): void {
        this.hpPagination.currentPage = 1;
        this.fetchHyperpureOrders();
    }

    /**
     * Handles sorting for the Hyperpure orders table.
     * @param column Column key to sort by.
     */
    onHpSort(column: string): void {
        if (this.hpSortBy === column) {
            this.hpSortOrder = this.hpSortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.hpSortBy = column;
            this.hpSortOrder = 'desc';
        }
        this.fetchHyperpureOrders();
    }

    /**
     * Handles pagination change for Hyperpure orders.
     * @param page New page number.
     */
    onHpPageChange(page: number): void {
        this.hpPagination.currentPage = page;
        this.fetchHyperpureOrders();
    }

    /**
     * Resets pagination and fetches Hyperpure orders when page size changes.
     */
    onHpPageSizeChange(pageSize: number): void {
        this.hpPagination.pageSize = pageSize;
        this.hpPagination.currentPage = 1;
        this.fetchHyperpureOrders();
    }

    /**
     * Triggers an invoice download for a specific Hyperpure order.
     * @param order The Hyperpure order object.
     */
    downloadHpInvoice(order: HyperpureOrder): void {
        this.hyperpureOrderService.downloadInvoice(order.orderNumber, 'combined');
    }

    /**
     * Returns the appropriate CSS class for a status badge.
     * @param status The order status string.
     */
    getHpStatusBadgeClass(status: string): string {
        const statusMap: { [key: string]: string } = {
            'PLACED': 'badge-primary',
            'DELIVERED': 'badge-success',
            'CANCELLED': 'badge-danger',
            'PENDING': 'badge-warning'
        };
        return statusMap[status] || 'badge-secondary';
    }

    /**
     * Formats a date string or object into a localized Indian date format.
     * @param date Date to format.
     */
    formatHpDate(date: Date | string): string {
        if (!date) return '-';
        const d = new Date(date);
        return d.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Formats a number into Indian currency (Rupees) string.
     * @param amount Amount to format.
     */
    formatHpCurrency(amount: number): string {
        if (amount === null || amount === undefined) return '₹0.00';
        return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    /**
     * Calculates the range of pages to display in the Hyperpure pagination UI.
     */
    get hpPaginationPages(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, this.hpPagination.currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(this.hpPagination.totalPages, startPage + maxPagesToShow - 1);
        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    }

    // ── DMart Orders ──

    /**
     * Fetches DMart orders from the DMart order service.
     * @param page The page number to fetch.
     */
    fetchDmartOrders(page: number = 1): void {
        if (!this.selectedEntityId) return;

        this.isDmartLoading = true;
        this.dmartPagination.page = page;

        const params: any = {
            page: this.dmartPagination.page,
            pageSize: this.dmartPagination.pageSize,
            search: this.dmartSearchQuery
        };

        this.dmartOrderService.getOrders(params)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    if (response.success) {
                        this.dmartOrders = response.data.orders || [];
                        this.dmartPagination.totalRecords = response.data.pagination.totalRecords || 0;
                        this.dmartPagination.totalPages = response.data.pagination.totalPages || 0;
                    } else {
                        this.dmartOrders = [];
                        this.dmartPagination.totalRecords = 0;
                        this.dmartPagination.totalPages = 0;
                    }
                    this.isDmartLoading = false;
                },
                (error: any) => {
                    console.error('Failed to fetch DMart orders', error);
                    this.sharedService.errorAlert('Failed to load DMart orders');
                    this.dmartOrders = [];
                    this.isDmartLoading = false;
                }
            );
    }

    /**
     * Handles pagination change for DMart orders.
     * @param page New page number.
     */
    onDmartPageChange(page: number): void {
        this.dmartPagination.page = page;
        this.fetchDmartOrders(page);
    }

    /**
     * Resets pagination and fetches DMart orders based on search query.
     */
    onDmartSearch(): void {
        this.dmartPagination.page = 1;
        this.fetchDmartOrders(1);
    }

    /**
     * Resets pagination and fetches DMart orders when page size changes.
     */
    onDmartPageSizeChange(pageSize: number): void {
        this.dmartPagination.pageSize = pageSize;
        this.dmartPagination.page = 1;
        this.fetchDmartOrders(1);
    }

    /**
     * Returns the appropriate CSS class for a DMart status badge.
     * @param status The order status code.
     */
    getDmartStatusBadgeClass(status: string): string {
        const statusMap: { [key: string]: string } = {
            'd': 'badge-success',      // Delivered
            'p': 'badge-primary',      // Placed
            'c': 'badge-danger',       // Cancelled
            'pending': 'badge-warning' // Pending
        };
        return statusMap[status.toLowerCase()] || 'badge-secondary';
    }

    /**
     * Formats a DMart date string (DD-MMM-YYYY) to display format.
     * @param date Date string to format.
     */
    formatDmartDate(date: string): string {
        if (!date) return '-';
        return date; // Already in format like "23-Feb-2026"
    }

    /**
     * Formats a number into Indian currency (Rupees) string.
     * @param amount Amount to format.
     */
    formatDmartCurrency(amount: number): string {
        if (amount === null || amount === undefined) return '₹0.00';
        return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    /**
     * Calculates the range of pages to display in the DMart pagination UI.
     */
    get dmartPaginationPages(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, this.dmartPagination.page - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(this.dmartPagination.totalPages, startPage + maxPagesToShow - 1);
        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    }

    /**
     * Downloads DMart invoice for a specific order
     * @param order The DMart order object
     */
    onDownloadDmartInvoice(order: any): void {
        if (!order || !order.orderId) {
            this.sharedService.errorAlert('Invalid order');
            return;
        }

        // Set loading state for this specific order
        order.isDownloading = true;

        this.dmartOrderService.downloadInvoice(order.orderId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    try {
                        // Extract blob from response
                        const blob = response.body;
                        
                        if (!blob || blob.size === 0) {
                            throw new Error('Empty file received');
                        }

                        // Get filename from Content-Disposition header or use default
                        let filename = `DMart_Invoice_${order.orderId}.pdf`;
                        const contentDisposition = response.headers.get('Content-Disposition');
                        if (contentDisposition) {
                            const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
                            if (matches != null && matches[1]) {
                                filename = matches[1].replace(/['"]/g, '');
                            }
                        }

                        // Create download link and trigger download
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = filename;
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();
                        
                        // Cleanup
                        setTimeout(() => {
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                        }, 100);

                        order.isDownloading = false;
                        this.sharedService.successAlert('Invoice downloaded successfully');
                    } catch (error) {
                        console.error('Error processing invoice download:', error);
                        order.isDownloading = false;
                        this.sharedService.errorAlert('Failed to process invoice download');
                    }
                },
                error: (error: any) => {
                    console.error('Failed to download DMart invoice', error);
                    order.isDownloading = false;
                    
                    let errorMessage = 'Failed to download invoice';
                    if (error.status === 404) {
                        errorMessage = 'Invoice not found';
                    } else if (error.status === 401 || error.status === 403) {
                        errorMessage = 'Unauthorized to download invoice';
                    } else if (error.status === 500) {
                        errorMessage = 'Server error. Please try again later';
                    }
                    
                    this.sharedService.errorAlert(errorMessage);
                }
            });
    }

    // ── DeliverIt Orders ──

    /**
     * Fetches DeliverIt orders using the DeliverIt order service.
     */
    fetchDeliveritOrders(): void {
        if (!this.selectedEntityId) return;

        this.isDeliveritLoading = true;
        const params = {
            page: this.deliveritPagination.currentPage,
            limit: this.deliveritPagination.pageSize,
            search: this.deliveritSearchTerm,
            sort_by: this.deliveritSortBy,
            sort_order: this.deliveritSortOrder,
            status: this.deliveritSelectedStatus,
            entityId: this.selectedEntityId
        };

        this.deliveritOrderService.getOrders(params)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response.success) {
                        this.deliveritOrders = response.data.orders || [];
                        this.deliveritPagination.totalOrders = response.data.pagination?.total || 0;
                        this.deliveritPagination.totalPages = response.data.pagination?.totalPages || 0;
                    }
                    this.isDeliveritLoading = false;
                },
                error: (error) => {
                    console.error('Error loading DeliverIt orders:', error);
                    this.isDeliveritLoading = false;
                }
            });
    }

    /**
     * Triggers a new DeliverIt order fetch with reset pagination on search.
     */
    onDeliveritSearch(): void {
        this.deliveritPagination.currentPage = 1;
        this.fetchDeliveritOrders();
    }

    /**
     * Triggers a new DeliverIt order fetch when status filter changes.
     */
    onDeliveritStatusChange(): void {
        this.deliveritPagination.currentPage = 1;
        this.fetchDeliveritOrders();
    }

    /**
     * Handles pagination change for DeliverIt orders.
     * @param page New page number.
     */
    onDeliveritPageChange(page: number): void {
        this.deliveritPagination.currentPage = page;
        this.fetchDeliveritOrders();
    }

    /**
     * Resets pagination and fetches DeliverIt orders when page size changes.
     */
    onDeliveritPageSizeChange(pageSize: number): void {
        this.deliveritPagination.pageSize = pageSize;
        this.deliveritPagination.currentPage = 1;
        this.fetchDeliveritOrders();
    }

    /**
     * Triggers an invoice download for a specific DeliverIt order.
     * @param order The DeliverIt order object.
     */
    downloadDeliveritInvoice(order: DeliveritOrder): void {
        this.deliveritOrderService.getInvoiceSignedUrl(order.orderId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response.success && response.data.signedUrl) {
                        window.open(response.data.signedUrl, '_blank');
                    } else {
                        this.sharedService.warningAlert(response.message || 'Invoice not available yet');
                    }
                },
                error: (error) => {
                    console.error('Failed to fetch DeliverIt invoice', error);
                    this.sharedService.errorAlert('Failed to download invoice');
                }
            });
    }

    /**
     * Returns the appropriate CSS class for a DeliverIt status badge.
     * @param status The order status string.
     */
    getDeliveritStatusBadgeClass(status: string): string {
        const statusMap: { [key: string]: string } = {
            'NEW': 'badge-primary',
            'PICK-PACK': 'badge-info',
            'INVOICING': 'badge-purple',
            'ASSIGNED': 'badge-secondary',
            'OUT FOR DELIVERY': 'badge-warning',
            'DELIVERED': 'badge-success',
            'RETURNED': 'badge-danger',
            'CLOSED': 'badge-secondary'
        };
        return statusMap[status] || 'badge-secondary';
    }

    /**
     * Formats a date string or object into a localized Indian date format.
     * @param date Date to format.
     */
    formatDeliveritDate(date: Date | string): string {
        if (!date) return '-';
        const d = new Date(date);
        return d.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Formats a number into Indian currency (Rupees) string.
     * @param amount Amount to format.
     */
    formatDeliveritCurrency(amount: number): string {
        if (amount === null || amount === undefined) return '₹0.00';
        return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    /**
     * Calculates the range of pages to display in the DeliverIt pagination UI.
     */
    get deliveritPaginationPages(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, this.deliveritPagination.currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(this.deliveritPagination.totalPages, startPage + maxPagesToShow - 1);
        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    }
}
